/**
 * Marketing Attribution Capture
 *
 * Captures UTM parameters + ad-network click IDs on the operator page, persists
 * them first-party (root-domain cookie + localStorage supplement, 30-day expiry),
 * and coalesces URL -> stored -> empty for the anonymous-login body.
 *
 * Contract: etp-bff BUSIE-3150 plan §9 (login body shape) + §3 (sanitization
 * mirror). The server re-sanitizes defensively and never rejects on attribution;
 * client-side stripping here just avoids leaking PII query params off the page.
 * Self-contained — no @busie/* dependencies.
 */

export interface Attribution {
  utmSource?: string
  utmMedium?: string
  utmCampaign?: string
  utmTerm?: string
  utmContent?: string
  /** Open-ended ad-network click IDs (gclid, fbclid, msclkid, …). */
  clickIds?: Record<string, string>
  /** Referring page, origin only (PII stripped). */
  referrer?: string
  /** Landing page: origin + path + whitelisted (utm_* + click-ID) params only. */
  landingPage?: string
  /** 'url' = params on the current page; 'storage' = carried over from a prior visit. */
  attributionSource?: 'url' | 'storage'
}

interface StoredAttribution {
  attribution: Attribution
  capturedAt: number
}

const STORAGE_KEY = 'busie_attribution'
const COOKIE_NAME = 'busie_attribution'
const MAX_AGE_DAYS = 30
const MAX_AGE_MS = MAX_AGE_DAYS * 24 * 60 * 60 * 1000

const UTM_MAP: Record<string, keyof Attribution> = {
  utm_source: 'utmSource',
  utm_medium: 'utmMedium',
  utm_campaign: 'utmCampaign',
  utm_term: 'utmTerm',
  utm_content: 'utmContent',
}

// Known irregular click-ID params + a forward-compatible suffix heuristic so new
// ad networks are absorbed without code churn (server accepts any /^[a-z0-9_-]{1,64}$/ key).
const KNOWN_CLICK_IDS = new Set([
  'gclid',
  'dclid',
  'gbraid',
  'wbraid',
  'fbclid',
  'msclkid',
  'twclid',
  'ttclid',
  'li_fat_id',
  'epik',
  'rdt_cid',
  'sccid',
])
const CLICK_ID_HEURISTIC = /(clid|_cid|clickid)$/
const CLICK_ID_KEY_RE = /^[a-z0-9_-]{1,64}$/

// §3 sanitization caps
const UTM_MAX = 255
const CLICK_ID_VALUE_MAX = 512
const CLICK_ID_MAX_KEYS = 20
const URL_MAX = 2048

/** Strip control chars + whitespace, trim; empty -> undefined. */
const clean = (value: string | null | undefined, max: number): string | undefined => {
  if (!value) return undefined
  // eslint-disable-next-line no-control-regex
  const stripped = value.replace(/[\x00-\x1F\x7F\s]+/g, ' ').trim()
  return stripped ? stripped.slice(0, max) : undefined
}

const isClickIdKey = (key: string): boolean =>
  KNOWN_CLICK_IDS.has(key) || CLICK_ID_HEURISTIC.test(key)

/** Origin only (scheme + host), PII stripped. */
const toOrigin = (url: string | undefined): string | undefined => {
  if (!url) return undefined
  try {
    return new URL(url).origin.slice(0, URL_MAX)
  } catch {
    return undefined
  }
}

/** Origin + path + whitelisted query params (utm_* + surviving click-IDs) only. */
const toLandingPage = (href: string, clickIdKeys: string[]): string | undefined => {
  try {
    const url = new URL(href)
    const kept = new URLSearchParams()
    url.searchParams.forEach((value, key) => {
      const k = key.toLowerCase()
      if (k in UTM_MAP || clickIdKeys.includes(k)) kept.set(k, value)
    })
    const query = kept.toString()
    return `${url.origin}${url.pathname}${query ? `?${query}` : ''}`.slice(0, URL_MAX)
  } catch {
    return undefined
  }
}

/** Parse attribution from a URL's query string + referrer. */
const parseFromUrl = (href: string, referrer: string): Attribution => {
  const attribution: Attribution = {}
  let params: URLSearchParams
  try {
    params = new URL(href).searchParams
  } catch {
    return attribution
  }

  Object.entries(UTM_MAP).forEach(([param, field]) => {
    const value = clean(params.get(param), UTM_MAX)
    if (value) (attribution[field] as string) = value
  })

  const clickIds: Record<string, string> = {}
  params.forEach((rawValue, rawKey) => {
    const key = rawKey.toLowerCase()
    if (!isClickIdKey(key) || !CLICK_ID_KEY_RE.test(key)) return
    if (Object.keys(clickIds).length >= CLICK_ID_MAX_KEYS) return
    const value = clean(rawValue, CLICK_ID_VALUE_MAX)
    if (value) clickIds[key] = value
  })
  if (Object.keys(clickIds).length > 0) attribution.clickIds = clickIds

  const referrerOrigin = toOrigin(referrer)
  if (referrerOrigin) attribution.referrer = referrerOrigin

  const landingPage = toLandingPage(href, Object.keys(clickIds))
  if (landingPage) attribution.landingPage = landingPage

  return attribution
}

/** URL carries attribution signal when a UTM or click ID is present (referrer alone is not). */
const hasSignal = (attribution: Attribution): boolean =>
  Boolean(
    attribution.utmSource ||
      attribution.utmMedium ||
      attribution.utmCampaign ||
      attribution.utmTerm ||
      attribution.utmContent ||
      (attribution.clickIds && Object.keys(attribution.clickIds).length > 0)
  )

/**
 * Cookie-domain candidates for a hostname, broadest first.
 *
 * Every candidate is a suffix of `hostname` with at least two labels. A naive
 * last-two-labels guess is wrong on multi-part TLDs: `www.example.co.uk` yields
 * `co.uk`, which the browser rejects as a public suffix — so the cookie is never
 * written and attribution degrades to localStorage-only, losing cross-subdomain
 * carry-over. Rather than ship (and age) a copy of the public-suffix list, we let
 * the browser be the authority: `pickAcceptedDomain` walks these broadest-first
 * and keeps the first one accepted, which lands on the registrable domain.
 *
 * Empty for hosts that cannot carry a domain-scoped cookie (localhost, IPs,
 * single-label intranet hosts) — those get a host-only cookie.
 */
export const cookieDomainCandidates = (hostname: string): string[] => {
  if (
    hostname === 'localhost' ||
    hostname.endsWith('.localhost') ||
    /^[\d.]+$/.test(hostname) ||
    hostname.includes(':')
  ) {
    return [] // host-only cookie for localhost / IPs
  }
  const parts = hostname.split('.')
  const candidates: string[] = []
  for (let i = parts.length - 2; i >= 0; i--) candidates.push(parts.slice(i).join('.'))
  return candidates
}

/**
 * Broadest candidate the browser actually accepts, or null for a host-only cookie.
 * Probes with a throwaway cookie carrying the same attributes as the real one
 * (a `Secure` probe on http would be rejected for the wrong reason), and deletes
 * each probe with those same attributes so no litter accumulates.
 */
const pickAcceptedDomain = (candidates: string[], secure: boolean): string | null => {
  for (const domain of candidates) {
    const name = `busie_cd_${Math.random().toString(36).slice(2, 8)}`
    const attrs = `path=/; domain=.${domain}; SameSite=Lax${secure ? '; Secure' : ''}`
    document.cookie = `${name}=1; ${attrs}`
    const accepted = new RegExp(`(?:^|; )${name}=`).test(document.cookie)
    document.cookie = `${name}=; ${attrs}; max-age=0`
    if (accepted) return domain
  }
  return null // cookies blocked entirely — localStorage supplement carries it
}

/** Resolved once per page: `persist` may run more than once, the probe should not. */
let resolvedCookieDomain: string | null | undefined

const cookieDomain = (secure: boolean): string | null => {
  if (resolvedCookieDomain === undefined) {
    resolvedCookieDomain = pickAcceptedDomain(
      cookieDomainCandidates(window.location.hostname),
      secure
    )
  }
  return resolvedCookieDomain
}

const persist = (attribution: Attribution): void => {
  const payload = JSON.stringify({ attribution, capturedAt: Date.now() } as StoredAttribution)
  try {
    const secure = window.location.protocol === 'https:'
    const domain = cookieDomain(secure)
    const maxAge = MAX_AGE_DAYS * 24 * 60 * 60
    let cookie = `${COOKIE_NAME}=${encodeURIComponent(payload)}; path=/; max-age=${maxAge}; SameSite=Lax`
    if (domain) cookie += `; domain=.${domain}`
    if (secure) cookie += '; Secure'
    document.cookie = cookie
  } catch {
    /* cookie unavailable — localStorage supplement below */
  }
  try {
    localStorage.setItem(STORAGE_KEY, payload)
  } catch {
    /* private browsing — cookie above is the fallback */
  }
}

const readStored = (): Attribution | undefined => {
  const raw = readCookieValue() ?? readLocalStorageValue()
  if (!raw) return undefined
  try {
    const parsed = JSON.parse(raw) as StoredAttribution
    if (!parsed?.capturedAt || Date.now() - parsed.capturedAt > MAX_AGE_MS) return undefined
    return parsed.attribution
  } catch {
    return undefined
  }
}

const readCookieValue = (): string | null => {
  try {
    const match = document.cookie.match(new RegExp(`(?:^|; )${COOKIE_NAME}=([^;]*)`))
    return match ? decodeURIComponent(match[1]) : null
  } catch {
    return null
  }
}

const readLocalStorageValue = (): string | null => {
  try {
    return localStorage.getItem(STORAGE_KEY)
  } catch {
    return null
  }
}

/**
 * Capture attribution from the current page and persist it when the URL carries
 * a fresh signal. Call at SDK load, on the operator page (real marketing URL).
 * Never persists an empty capture — a bare page load must not clobber a prior touch.
 */
export const captureAndPersistAttribution = (): void => {
  if (typeof window === 'undefined') return
  const captured = parseFromUrl(window.location.href, document.referrer)
  if (hasSignal(captured)) persist(captured)
}

/**
 * Resolve the attribution object to send at anonymous login.
 * Coalesce: current-URL signal -> stored (within 30 days) -> undefined (omit).
 */
export const getAttributionForLogin = (): Attribution | undefined => {
  if (typeof window === 'undefined') return undefined

  const captured = parseFromUrl(window.location.href, document.referrer)
  if (hasSignal(captured)) return { ...captured, attributionSource: 'url' }

  const stored = readStored()
  if (stored) return { ...stored, attributionSource: 'storage' }

  return undefined
}
